import { useState } from 'react';
import { Archive, ArchiveRestore, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { usePermissions } from '@/hooks/use-permissions';
import {
  usePlanningWorkGroups,
  useCreatePlanningWorkGroup,
  useUpdatePlanningWorkGroup,
  useDeletePlanningWorkGroup,
  usePlanningActivities,
  useCreatePlanningActivity,
  useUpdatePlanningActivity,
  useDeletePlanningActivity,
} from '@/hooks/use-planning';
import type { PlanningWorkGroup, PlanningActivity } from '@/features/planning/types';

type CatalogTab = 'work-groups' | 'activities';

export function PlanningCatalogPage() {
  const { hasCapability } = usePermissions();
  const canManage = hasCapability('planning.catalog.manage');

  const [activeTab, setActiveTab] = useState<CatalogTab>('work-groups');
  const [showInactive, setShowInactive] = useState(false);

  const [workGroupDialogOpen, setWorkGroupDialogOpen] = useState(false);
  const [editingWorkGroup, setEditingWorkGroup] = useState<PlanningWorkGroup | null>(null);

  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<PlanningActivity | null>(null);

  const { data: workGroups = [], isLoading: workGroupsLoading } = usePlanningWorkGroups(showInactive);
  const createWorkGroup = useCreatePlanningWorkGroup();
  const updateWorkGroup = useUpdatePlanningWorkGroup();
  const deleteWorkGroup = useDeletePlanningWorkGroup();

  const { data: activities = [], isLoading: activitiesLoading } = usePlanningActivities(showInactive);
  const createActivity = useCreatePlanningActivity();
  const updateActivity = useUpdatePlanningActivity();
  const deleteActivity = useDeletePlanningActivity();

  const handleOpenWorkGroupDialog = (workGroup?: PlanningWorkGroup) => {
    setEditingWorkGroup(workGroup ?? null);
    setWorkGroupDialogOpen(true);
  };

  const handleOpenActivityDialog = (activity?: PlanningActivity) => {
    setEditingActivity(activity ?? null);
    setActivityDialogOpen(true);
  };

  const handleArchiveWorkGroup = async (workGroup: PlanningWorkGroup) => {
    try {
      await updateWorkGroup.mutateAsync({
        id: workGroup.id,
        data: { isActive: !workGroup.isActive },
      });
      toast({ title: workGroup.isActive ? 'Work group archived' : 'Work group restored' });
    } catch (error) {
      toast({ title: 'Error', description: normalizeError(error).message, variant: 'destructive' });
    }
  };

  const handleDeleteWorkGroup = async (workGroup: PlanningWorkGroup) => {
    const confirmed = window.confirm(`Delete "${workGroup.name}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteWorkGroup.mutateAsync(workGroup.id);
      toast({ title: 'Work group deleted' });
    } catch (error) {
      toast({ title: 'Error', description: normalizeError(error).message, variant: 'destructive' });
    }
  };

  const handleArchiveActivity = async (activity: PlanningActivity) => {
    try {
      await updateActivity.mutateAsync({
        id: activity.id,
        data: { isActive: !activity.isActive },
      });
      toast({ title: activity.isActive ? 'Activity archived' : 'Activity restored' });
    } catch (error) {
      toast({ title: 'Error', description: normalizeError(error).message, variant: 'destructive' });
    }
  };

  const handleDeleteActivity = async (activity: PlanningActivity) => {
    const confirmed = window.confirm(`Delete "${activity.name}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteActivity.mutateAsync(activity.id);
      toast({ title: 'Activity deleted' });
    } catch (error) {
      toast({ title: 'Error', description: normalizeError(error).message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Planning Catalog</h1>
          <p className="text-muted-foreground">Manage custom work groups and activities</p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor="show-inactive" className="text-sm">Show archived</Label>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-border">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'work-groups'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('work-groups')}
        >
          Work Groups
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'activities'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('activities')}
        >
          Activities
        </button>
      </div>

      {activeTab === 'work-groups' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Work Groups</CardTitle>
              <CardDescription>Custom work groups for planning items</CardDescription>
            </div>
            {canManage && (
              <Button onClick={() => handleOpenWorkGroupDialog()} size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Work Group
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {workGroupsLoading ? (
              <p className="text-sm text-muted-foreground">Loading work groups...</p>
            ) : workGroups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No work groups found.</p>
            ) : (
              <div className="space-y-2">
                {workGroups.map((wg) => (
                  <div
                    key={wg.id}
                    className={`flex items-center justify-between rounded-md border p-3 ${
                      wg.isActive ? 'bg-card' : 'bg-muted/50 opacity-60'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{wg.name}</span>
                        {!wg.isActive && (
                          <Badge variant="secondary" className="text-xs">Archived</Badge>
                        )}
                      </div>
                      {wg.description && (
                        <p className="text-xs text-muted-foreground truncate">{wg.description}</p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex gap-1 ml-4">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => handleOpenWorkGroupDialog(wg)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => handleArchiveWorkGroup(wg)}
                          title={wg.isActive ? 'Archive' : 'Restore'}
                        >
                          {wg.isActive ? (
                            <Archive className="h-4 w-4" />
                          ) : (
                            <ArchiveRestore className="h-4 w-4" />
                          )}
                        </Button>
                        {!wg.isActive && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteWorkGroup(wg)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'activities' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Activities</CardTitle>
              <CardDescription>Custom activities for planning items</CardDescription>
            </div>
            {canManage && (
              <Button onClick={() => handleOpenActivityDialog()} size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Activity
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {activitiesLoading ? (
              <p className="text-sm text-muted-foreground">Loading activities...</p>
            ) : activities.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activities found.</p>
            ) : (
              <div className="space-y-2">
                {activities.map((act) => (
                  <div
                    key={act.id}
                    className={`flex items-center justify-between rounded-md border p-3 ${
                      act.isActive ? 'bg-card' : 'bg-muted/50 opacity-60'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{act.name}</span>
                        {!act.isActive && (
                          <Badge variant="secondary" className="text-xs">Archived</Badge>
                        )}
                      </div>
                      {act.description && (
                        <p className="text-xs text-muted-foreground truncate">{act.description}</p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex gap-1 ml-4">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => handleOpenActivityDialog(act)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => handleArchiveActivity(act)}
                          title={act.isActive ? 'Archive' : 'Restore'}
                        >
                          {act.isActive ? (
                            <Archive className="h-4 w-4" />
                          ) : (
                            <ArchiveRestore className="h-4 w-4" />
                          )}
                        </Button>
                        {!act.isActive && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteActivity(act)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <WorkGroupFormDialog
        open={workGroupDialogOpen}
        onOpenChange={setWorkGroupDialogOpen}
        workGroup={editingWorkGroup}
        onCreate={createWorkGroup.mutateAsync}
        onUpdate={updateWorkGroup.mutateAsync}
        isPending={createWorkGroup.isPending || updateWorkGroup.isPending}
      />

      <ActivityFormDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        activity={editingActivity}
        onCreate={createActivity.mutateAsync}
        onUpdate={updateActivity.mutateAsync}
        isPending={createActivity.isPending || updateActivity.isPending}
      />
    </div>
  );
}

interface WorkGroupFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workGroup: PlanningWorkGroup | null;
  onCreate: (data: { name: string; description?: string }) => Promise<PlanningWorkGroup>;
  onUpdate: (params: { id: string; data: { name?: string; description?: string } }) => Promise<PlanningWorkGroup>;
  isPending: boolean;
}

function WorkGroupFormDialog({
  open,
  onOpenChange,
  workGroup,
  onCreate,
  onUpdate,
  isPending,
}: WorkGroupFormDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!workGroup;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && workGroup) {
      setName(workGroup.name);
      setDescription(workGroup.description ?? '');
    } else if (nextOpen) {
      setName('');
      setDescription('');
    }
    setError(null);
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }

    try {
      if (isEdit) {
        await onUpdate({
          id: workGroup.id,
          data: {
            name: trimmedName,
            description: description.trim() || undefined,
          },
        });
        toast({ title: 'Work group updated' });
      } else {
        await onCreate({
          name: trimmedName,
          description: description.trim() || undefined,
        });
        toast({ title: 'Work group created' });
      }
      onOpenChange(false);
    } catch (err) {
      setError(normalizeError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Work Group' : 'Add Work Group'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the work group details.' : 'Create a new custom work group.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="wg-name">Name</Label>
            <Input
              id="wg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Supervisor Tasks"
              maxLength={200}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wg-description">Description (optional)</Label>
            <Textarea
              id="wg-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this work group..."
              rows={3}
              className="resize-none"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ActivityFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: PlanningActivity | null;
  onCreate: (data: { name: string; description?: string }) => Promise<PlanningActivity>;
  onUpdate: (params: { id: string; data: { name?: string; description?: string } }) => Promise<PlanningActivity>;
  isPending: boolean;
}

function ActivityFormDialog({
  open,
  onOpenChange,
  activity,
  onCreate,
  onUpdate,
  isPending,
}: ActivityFormDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!activity;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && activity) {
      setName(activity.name);
      setDescription(activity.description ?? '');
    } else if (nextOpen) {
      setName('');
      setDescription('');
    }
    setError(null);
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }

    try {
      if (isEdit) {
        await onUpdate({
          id: activity.id,
          data: {
            name: trimmedName,
            description: description.trim() || undefined,
          },
        });
        toast({ title: 'Activity updated' });
      } else {
        await onCreate({
          name: trimmedName,
          description: description.trim() || undefined,
        });
        toast({ title: 'Activity created' });
      }
      onOpenChange(false);
    } catch (err) {
      setError(normalizeError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Activity' : 'Add Activity'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the activity details.' : 'Create a new custom activity.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="act-name">Name</Label>
            <Input
              id="act-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Facebook"
              maxLength={200}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="act-description">Description (optional)</Label>
            <Textarea
              id="act-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this activity..."
              rows={3}
              className="resize-none"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
